import { Injectable } from '@angular/core';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { HttpErrorResponse } from '@angular/common/http';
import { SelectItem, SelectItemClient } from "../../../NSwag/nswag-api-sps";
import { VacancyClient } from "../../../NSwag/nswag-api-pimmaJob";
import { SpSelectItems } from '../Enum/SpSelectItems';
import { environment } from "../../../environment/environment";

export interface SpProvider
{
  [spName: string]: ReplaySubject<SelectItem[]>;
}

@Injectable()
export class SpService
{
  spRegion: SelectItem[] = [];
  spDistrict: SelectItem[] = [];
  spInstitution: SelectItem[] = [];
  spDepartment: SelectItem[] = [];
  spPosition: SelectItem[] = [];
  spSubjects: SelectItem[] = [];
  spTypePrezidentSchools: SelectItem[] = [];
  spEducationLevels: SelectItem[] = [];
  spTypeOfPositions: SelectItem[] = [];
  spCategoryOfPositions: SelectItem[] = [];
  spEducationalOrganizations: SelectItem[] = [];
  spLanguages: SelectItem[] = [];
  spApplicantFileTypes: SelectItem[] = [];

  private downloadedSps: string[] = [];

  provider: SpProvider = {};
  initialDownloadSps: SpSelectItems[];

  constructor(
    private selectItemClient: SelectItemClient,
    private vacancyClient: VacancyClient,
    private _translocoService: TranslocoService
  )
  {
    this.initialDownloadSps = [
      SpSelectItems.SPRegions,
      SpSelectItems.SPDistricts,
      SpSelectItems.SPInstitutions,
      SpSelectItems.SpSubjects,
      SpSelectItems.SpTypePrezidentSchools,
      SpSelectItems.SpEducationLevels,
      SpSelectItems.SpEducationalOrganization,
      SpSelectItems.SpTypeOfPosition,
      SpSelectItems.SpCategoryOfPosition,
      SpSelectItems.SpLanguage,
      SpSelectItems.SpApplicantFileTypes
    ];

    for (const init of this.initialDownloadSps)
    {

      this.provider[init] = new ReplaySubject<SelectItem[]>(1);
    }

    this.Initialize(this.initialDownloadSps);

    this.initializeVacancySp();
  }
  public initializeVacancySp(): void
  {
    this.vacancyClient.getDepartments(undefined).subscribe({
      next: (vacancy) =>
      {
        this.spDepartment = vacancy as [];
      }
    });

    this.vacancyClient.getPositions(undefined).subscribe(
      {
        next: (position) =>
        {
          this.spPosition = position as [];
        }
      }
    )
  }

  public Initialize(
    initialDownloadSps: SpSelectItems[] = this.initialDownloadSps
  )
  {
    from(initialDownloadSps)
      .pipe(
        mergeMap((sp, index) =>
        {
          return this.updateSp(sp).pipe(
            catchError((err: HttpErrorResponse) =>
            {
              console.error(
                'Error downloading sp from api. Sp: ' +
                sp.toString(),
                err
              );
              return of(new Array<SelectItem>(0));
            }),
            map(items =>
            {
              return {
                items: items,
                sp: sp,
                index: index,
              };
            })
          );
        })
      )
      .subscribe(data =>
      {
        this.provider[data.sp].next(data.items);

        if (!environment.production)
        {
        }
      });
  }

  getSpDistrict(regionId: string)
  {
    if (!(regionId)) return;

    this.selectItemClient
      .getSelectItemsByRoot(SpSelectItems.SPDistricts, regionId)
      .subscribe(
        item =>
        (this.spDistrict = item.map(x =>
        {
          x.value = <string>x.code;
          return x;
        }))
      );
  }

  getLabelByValue(selectItem: SelectItem[], value: any): string
  {
    if (!selectItem || selectItem.length < 1) return '';

    return selectItem.find(f => f.value == <string>value)?.label ?? '';
  }

  public updateSp(sp: SpSelectItems): Observable<SelectItem[]>
  {
    switch (sp)
    {
      case SpSelectItems.SPRegions:
        return this.downloadSp(
          SpSelectItems.SPRegions,
          this.spRegion,
          null,
          true
        );

      case SpSelectItems.SPDistricts:
        return this.downloadSp(
          SpSelectItems.SPDistricts,
          this.spDistrict,
          null,
          true
        );
      case SpSelectItems.SPInstitutions:
        return this.downloadSp(
          SpSelectItems.SPInstitutions,
          this.spInstitution,
          null,
          false
        );
      case SpSelectItems.SpSubjects:
        return this.downloadSp(
          SpSelectItems.SpSubjects,
          this.spSubjects,
          null,
          false
        );
      case SpSelectItems.SpTypePrezidentSchools:
        return this.downloadSp(
          SpSelectItems.SpTypePrezidentSchools,
          this.spTypePrezidentSchools,
          null,
          false
        );
      case SpSelectItems.SpEducationalOrganization:
        return this.downloadSp(
          SpSelectItems.SpEducationalOrganization,
          this.spEducationalOrganizations,
          null,
          false
        );
      case SpSelectItems.SpEducationLevels:
        return this.downloadSp(
          SpSelectItems.SpEducationLevels,
          this.spEducationLevels,
          null,
          false
        );
      case SpSelectItems.SpCategoryOfPosition:
        return this.downloadSp(
          SpSelectItems.SpCategoryOfPosition,
          this.spCategoryOfPositions,
          null,
          false
        );
      case SpSelectItems.SpTypeOfPosition:
        return this.downloadSp(
          SpSelectItems.SpTypeOfPosition,
          this.spTypeOfPositions,
          null,
          false
        );
      case SpSelectItems.SpLanguage:
        return this.downloadSp(
          SpSelectItems.SpLanguage,
          this.spLanguages
        );
      case SpSelectItems.SpApplicantFileTypes:
        return this.downloadSp(
          SpSelectItems.SpApplicantFileTypes,
          this.spApplicantFileTypes
        );

    }
  }

  public getSpSelectItems(sp: SpSelectItems): SelectItem[]
  {
    switch (sp)
    {
      case SpSelectItems.SPRegions:
        return this.spRegion;
      case SpSelectItems.SPDistricts:
        return this.spDistrict;
      case SpSelectItems.SPInstitutions:
        return this.spInstitution;
      case SpSelectItems.SpSubjects:
        return this.spSubjects;
      case SpSelectItems.SpTypePrezidentSchools:
        return this.spTypePrezidentSchools;
      case SpSelectItems.SpEducationLevels:
        return this.spEducationLevels;
      case SpSelectItems.SpTypeOfPosition:
        return this.spTypeOfPositions;
      case SpSelectItems.SpCategoryOfPosition:
        return this.spCategoryOfPositions;
      case SpSelectItems.SpEducationalOrganization:
        return this.spEducationalOrganizations
      case SpSelectItems.SpLanguage:
        return this.spLanguages
      case SpSelectItems.SpApplicantFileTypes:
        return this.spApplicantFileTypes
    }
  }

  public downloadSp(
    sp: SpSelectItems,
    property: SelectItem[],
    filterP?: (item: SelectItem) => boolean,
    isCodeValue?: boolean,
    downloadBy?: 'original' | 'code' | 'rootCode',
    codeId?: string,
    sliceFirst?: boolean,
    mapP?: (item: SelectItem) => SelectItem,
    sortP?: (first: SelectItem, second: SelectItem) => number
  ): Observable<SelectItem[]>
  {
    let observable: Observable<SelectItem[]>;
    switch (downloadBy)
    {
      case 'code':
        observable = this.selectItemClient.getSelectItemsByCode(
          sp,
          codeId
        );
        break;

      case 'rootCode':
        observable = this.selectItemClient.getSelectItemsByRoot(
          sp,
          codeId
        );
        break;
      default:
        observable = this.selectItemClient.getSelectItems(sp);
        break;
    }

    return observable.pipe(
      map(items =>
      {
        if (!environment.production)
        {
          if (!this.downloadedSps.includes(sp))
          {
            this.downloadedSps.push(sp);          }        }

        let localValue: SelectItem[] = [];

        if (filterP) localValue = items.filter(f => filterP(f));
        else localValue = sliceFirst ? items.slice(1) : items;

        if (isCodeValue)
          localValue = localValue.map(m =>
          {
            m.value = m.code;
            return m;
          });

        if (mapP) localValue = localValue.map(v => mapP(v));

        if (sortP) localValue = localValue.sort((f, s) => sortP(f, s));

        property.splice(0, property.length - 1);
        localValue.forEach(x => property.push(x));
        return [...property];
      })
    );
  }
}
